import React from 'react'
import { camelCase } from 'lodash'
import PQueue from 'p-queue'
import i18next from 'i18next'
import TextAreaAutosize from 'react-textarea-autosize'
import { FileDetails } from '../ui/FileDetails'
import { extractFileExtension, type FileSelectExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { UiChatFilesMap } from './ZapBot'
import { Spinner } from '../ui/Spinner'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'
import { type FileId, generateFileId } from '../../../shared/domain/files/types.no-deps'
import type { AiChatFile, ChatId } from '../../../shared/domain/aiChats/types.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { uploadBlobWithoutQueue } from '../upload/uploadBlobWithoutQueue'
import { getMimeTypeFromExtension } from '../../../shared/lib/mimeTypes.no-deps'
import type { ApiClient } from '../util/ApiClient'
import { MAX_CHAT_FILE_CONTENTS_LENGTH } from '../../../shared/lib/aws/lambda/ingestEntity.no-deps'
import { abbreviateNumber } from '../../../shared/lib/fileSize'
import { ZapBotContext } from './ZapBotContext'

type Props = {
    apiClient: ApiClient
    deps: CommonDeps
    featureFlags: Pick<CamelCaseFlags, 'aiZapBotFiles' | 'aiCredits'>
    fetchChatId: () => Promise<ChatId>
    onSubmit: (prompt: string, files: FileId[]) => void
    onAbort: () => void
}

const SUPPORTED_FILE_EXTENSIONS: FileSelectExtension[] = ['.pdf', '.pptx', '.docx', '.doc']

export function ZapBotPromptInput({ apiClient, deps, featureFlags, fetchChatId, ...props }: Props) {
    const { hasCredits, isLoading, promptAnswer } = React.useContext(ZapBotContext)

    const [prompt, setPrompt] = React.useState('')
    const [chatFilesMap, setChatFilesMap] = React.useState<UiChatFilesMap>(new Map())
    const polledChatFiles = React.useRef<AiChatFile[]>([])
    const filesPollTimer = React.useRef<number>(null)
    const [shouldPollFileStatus, setShouldPollFileStatus] = React.useState(false)
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null)
    const fileInputRef = React.useRef<HTMLInputElement>(null)
    const chatFileListRef = React.useRef<HTMLUListElement>(null)

    React.useEffect(() => {
        if (!shouldPollFileStatus) {
            return
        }

        const poller = async () => {
            if (!polledChatFiles.current.length) {
                setShouldPollFileStatus(false)
                return
            }

            await getFiles(polledChatFiles.current.map((file) => file.file))

            filesPollTimer.current = window.setTimeout(poller, 2000)
        }

        poller()

        return () => {
            if (filesPollTimer.current) {
                window.clearTimeout(filesPollTimer.current)
            }
        }
    }, [shouldPollFileStatus])

    const getFiles = async (files: FileId[]) => {
        const chatId = await fetchChatId()
        const { chatFiles } = await deps.httpClient.getAiChatFiles(chatId, files)

        // Update list of files to poll their status (only those still in progress).
        polledChatFiles.current = chatFiles.filter((file) => file.status === 'processing')

        setChatFilesMap((currentValue) => {
            const updatedMap = new Map(currentValue)
            chatFiles.forEach((file) => {
                if (!currentValue.has(file.file)) {
                    return
                }

                updatedMap.set(file.file, {
                    ...currentValue.get(file.file),
                    status: file.status,
                    errorCode: file.errorCode,
                })
            })

            return updatedMap
        })
    }

    const onPromptSubmit = () => {
        if (!prompt.length || !hasCredits) {
            return
        }

        const chatFiles = Array.from(chatFilesMap.values())
            .filter((file) => file.status === 'success')
            .map((file) => file.fileMetaId)

        return props.onSubmit(prompt, chatFiles)
    }

    const onPromptChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPrompt(event.target.value)
    }

    const onFilesSelected = async (files: FileList) => {
        const pQueue = new PQueue({ concurrency: 2 })

        const newChatFilesMap: UiChatFilesMap = new Map(
            Array.from(files).map((file) => {
                const fileId = generateFileId()

                return [
                    fileId,
                    {
                        fileMetaId: fileId,
                        file,
                        status: 'processing',
                        errorCode: null,
                        abortController: new AbortController(),
                    },
                ]
            })
        )

        // Adds newly selected files to the list of chat files for uploading / ingestion.
        setChatFilesMap((currentValue) => new Map([...currentValue, ...newChatFilesMap]))

        const chatId = await fetchChatId()

        newChatFilesMap.forEach(async (value, fileId) => {
            pQueue.add(async () => {
                const mimeType = getMimeTypeFromExtension(extractFileExtension(value.file.name))

                const fileUpload = await uploadBlobWithoutQueue(apiClient, new Blob([value.file], { type: mimeType }), {
                    fileName: value.file.name,
                    fileId,
                    abortSignal: value.abortController.signal,
                })

                const { chatFile: newChatFile } = await deps.httpClient.addAiChatFile(chatId, fileUpload.fileId)

                polledChatFiles.current.push(newChatFile)

                setShouldPollFileStatus(true)
            })
        })

        // Clears the file input to allow adding the same files again
        fileInputRef.current.value = null
    }

    const onInputClear = () => {
        setPrompt('')

        props.onSubmit('', [])

        textAreaRef.current.focus()
    }

    const onFileClear = (fileMetaId: FileId) => {
        setChatFilesMap((currentMap) => {
            const updated = new Map(currentMap)
            const entry = currentMap.get(fileMetaId)
            if (!entry) {
                return currentMap
            }

            entry.abortController.abort()
            updated.delete(fileMetaId)

            return updated
        })

        // Stop polling this file's status if it's still in progress.
        polledChatFiles.current = polledChatFiles.current.filter((file) => file.file !== fileMetaId)
    }

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter') {
            return
        }

        event.preventDefault()

        onPromptSubmit()
    }

    const onFileDetailsMounted = (index: number) => {
        // Horizontally scroll to the end of the list when a new file is added.
        if (index === chatFilesMap.size - 1) {
            chatFileListRef.current.scrollTo({
                behavior: 'smooth',
                left: chatFileListRef.current.scrollWidth,
            })
        }
    }

    const errors = Array.from(chatFilesMap.values())
        .filter(({ status }) => status === 'error')
        .map(({ file, errorCode }) => {
            const errorMsg =
                i18next.t(`appcore:ZapBot.errors.${camelCase(errorCode)}`, {
                    defaultValue: null,
                    fileName: file.name,
                    fileExtension: extractFileExtension(file.name),
                    maxChars: abbreviateNumber(MAX_CHAT_FILE_CONTENTS_LENGTH),
                }) || i18next.t('appcore:ZapBot.errors.unexpected', { fileName: file.name })

            return errorMsg
        })

    if (featureFlags.aiCredits && !hasCredits && promptAnswer.promptId) {
        // Display the error only if there's an answer rendered.
        errors.push(i18next.t('appcore:ZapBot.errors.outOfCredits'))
    }

    return (
        <>
            <div className="ZapBot_input">
                {featureFlags.aiZapBotFiles && (
                    <div className="ZapBot_attachment-button">
                        <label htmlFor="file-input" className="ZapBot_attachment-label">
                            <zaptic-icon icon-name="attachment"></zaptic-icon>
                            <span className="sr-only">Attach file</span>
                        </label>

                        <input
                            ref={fileInputRef}
                            type="file"
                            id="file-input"
                            className="ZapBot_attachment-input"
                            multiple
                            accept={SUPPORTED_FILE_EXTENSIONS.join(',')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const files = event.target.files
                                if (files?.length) {
                                    onFilesSelected(files)
                                }
                            }}
                        ></input>
                    </div>
                )}
                <div className="ZapBot_input-box">
                    <div className="ZapBot_prompt">
                        <TextAreaAutosize
                            ref={textAreaRef}
                            className="ZapBot_prompt-textarea"
                            placeholder={i18next.t('appcore:ZapBot.promptInputPlaceholder')}
                            value={prompt}
                            rows={1}
                            onChange={onPromptChange}
                            onKeyPress={onKeyPress}
                        />

                        {prompt.length > 0 && (
                            <button className="ZapBot_clear-button" onClick={onInputClear}>
                                <zaptic-icon icon-name="inverted-cross"></zaptic-icon>
                            </button>
                        )}
                    </div>

                    {chatFilesMap.size > 0 && (
                        <ul ref={chatFileListRef} className="ZapBot_input-files">
                            {Array.from(chatFilesMap.values()).map(({ fileMetaId, file, status }, index) => (
                                <li key={fileMetaId} className="ZapBot_input-file">
                                    <FileDetails
                                        disabled={false}
                                        loading={status === 'processing'}
                                        errored={status === 'error'}
                                        fileName={file.name}
                                        fileSize={file.size}
                                        fileExtension={extractFileExtension(file.name)}
                                        onClear={() => onFileClear(fileMetaId)}
                                        onMounted={() => onFileDetailsMounted(index)}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <button
                    className="ZapBot_send-button"
                    disabled={featureFlags.aiCredits && !hasCredits}
                    data-testid="zapbot-send-button"
                    onClick={onPromptSubmit}
                >
                    <span className="ZapBot_send-button-icon">
                        {!isLoading && <zaptic-icon icon-name="send"></zaptic-icon>}
                        {isLoading && <Spinner size={25} />}
                    </span>
                </button>
            </div>

            {errors.length > 0 && (
                <ul className="ZapBot_error-list">
                    {errors.map((message, index) => (
                        <ErrorItem key={index} message={message} />
                    ))}
                </ul>
            )}
        </>
    )
}

type ErrorItemProps = {
    message: string
}

function ErrorItem({ message }: ErrorItemProps) {
    return (
        <li className="ZapBot_error">
            <span className="ZapBot_error-icon">
                <zaptic-icon icon-name="info"></zaptic-icon>
            </span>{' '}
            {message}
        </li>
    )
}
